import React from 'react';
import { Box, Button, Chip, CircularProgress, Grid, Typography } from '@mui/material';
import type { IncidentDrawerDetailScope } from './incidentDrawerDetailTypes';
import { evidenceStatusChipMeta } from './incidentDrawerDetailStep2Utils';
import {
  resolvePatternEvidenceCameraLabel,
  resolvePatternEvidenceZoneLabel,
} from '../../utils/customerPlaceLabel';

export function IncidentDrawerRepresentativeEvidenceIntro({
  scope,
  preferSnapshotPreview = false,
}: {
  scope: IncidentDrawerDetailScope;
  /** When the dedicated clip player is mounted below, keep this as snapshot-only preview. */
  preferSnapshotPreview?: boolean;
}) {
  const s = scope as Record<string, any>;
  if (!s.groupedReviewMode) return null;

  const incident = s.selectedIncident || {};
  const snap = s.effectiveSnapshotStatus;
  const clip = s.effectiveClipStatus;
  const rawZoneId = s.safeText(incident.zone_id);
  const rawCameraId = s.safeText(incident.camera_id);
  const zoneLabel = resolvePatternEvidenceZoneLabel({
    zoneId: rawZoneId,
    zoneName: s.selectedZoneName,
  });
  const cameraLabel = resolvePatternEvidenceCameraLabel({
    cameraId: rawCameraId,
    cameraName: s.cameraName || s.selectedCameraName,
    zoneId: rawZoneId,
    zoneName: s.selectedZoneName,
    locationLine: s.groupedPrimaryLocationLine || s.reviewObservationLocationLine || s.primaryLocationLabel,
  });
  const ruleLabel = s.currentTitle || s.safeText(incident.incident_type) || '—';
  const snapshotUrl = s.latestSnapshotObjectUrl as string | null | undefined;
  const videoUrl = s.latestVideoObjectUrl as string | null | undefined;
  const snapshotLoading = Boolean(s.latestSnapshotLoading);
  const videoLoading = Boolean(s.latestVideoLoading);
  const clipPending = String(clip || '').toUpperCase() === 'PENDING';
  const capturedMs =
    s.snapshotFrameTimeMs
    || s.snapshotCapturedAtMs
    || s.evidenceTargetTimeMs
    || s.parseTimingMs?.(incident.created_at || incident.event_started_at || incident.started_at);
  const capturedLabel = capturedMs && s.formatTimingInstant ? s.formatTimingInstant(capturedMs) : '—';
  const showInlineVideo = Boolean(videoUrl) && !preferSnapshotPreview;

  return (
    <Box data-testid="representative-evidence-intro">
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.25, alignItems: 'center' }}>
        {(() => {
          const h = evidenceStatusChipMeta('snap', snap);
          return <Chip label={h.label} size="small" color={h.color} variant="outlined" sx={{ fontWeight: 800 }} />;
        })()}
        {(() => {
          const h = evidenceStatusChipMeta('clip', clip);
          return <Chip label={h.label} size="small" color={h.color} variant="outlined" sx={{ fontWeight: 800 }} />;
        })()}
        {clipPending && typeof s._refreshSelectedIncident === 'function' ? (
          <Button
            size="small"
            variant="text"
            onClick={() => void s._refreshSelectedIncident()}
            disabled={Boolean(s.drawerBusy)}
            data-testid="representative-refresh-clip-status"
            sx={{ fontWeight: 700, px: 0.5 }}
          >
            Refresh clip status
          </Button>
        ) : null}
        {String(clip || '').toUpperCase() === 'MISSING' ? (
          <Typography variant="caption" color="text.secondary" data-testid="representative-no-video-note">
            No video for this occurrence
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          mb: 1.25,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
          minHeight: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
        data-testid="representative-evidence-thumbnail"
      >
        {showInlineVideo ? (
          <Box
            component="video"
            src={videoUrl || undefined}
            poster={snapshotUrl || undefined}
            controls
            preload="metadata"
            sx={{ width: '100%', maxHeight: 420, display: 'block', bgcolor: '#000' }}
          />
        ) : snapshotLoading || (videoLoading && !snapshotUrl) ? (
          <CircularProgress size={28} />
        ) : snapshotUrl ? (
          <Box
            component="img"
            src={snapshotUrl}
            alt="Representative snapshot for this recurring risk pattern"
            sx={{ width: '100%', maxHeight: 220, objectFit: 'contain', display: 'block' }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
            {s.effectiveSnapshotStatus === 'AVAILABLE'
              ? 'Loading snapshot…'
              : (incident as any)?.clip_available_on_edge
                ? 'Evidence is still being prepared.'
                : 'No snapshot or clip is available.'}
          </Typography>
        )}
      </Box>

      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">
            Camera
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, minHeight: 22 }} data-testid="representative-evidence-camera-label">
            {cameraLabel}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">
            Area / zone
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, minHeight: 22 }} data-testid="representative-evidence-zone-label">
            {zoneLabel}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">
            Captured
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, minHeight: 22 }}>
            {capturedLabel}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">
            Detection
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {ruleLabel}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
}
